import { customizationEnabled } from './customization-util';
// src/app/infra/bootstrap-config.ts
export interface BootstrapConfig {
    vid: string;
    dashedVid: string;
    nzVid: string;
    isNdePath: boolean;
    prefix: string;
    animationPath: string;
    /**
     * Set of asset paths (e.g. "assets/images/...") present in the IZ customization package, or
     * `null` when no manifest is available (older packages). When present, the UI can resolve
     * custom assets in-memory instead of probing the server per record (URM-327431).
     */
    izAssets: Set<string> | null;
    /** Same as {@link izAssets} for the Network "CENTRAL_PACKAGE" customization package. */
    centralAssets: Set<string> | null;
}

declare global {
    interface Window {
        __BOOTSTRAP_CFG__?: BootstrapConfig;
    }
}

// A VID always has the form INST:VIEW (e.g. 32RUG_INST:32RUG_INST). In canonical
// URLs it appears as a path segment alongside docid/lang, which never contain a
// colon - so the colon-bearing segment uniquely identifies the VID.
const VID_SEGMENT_REGEX = /^[^/:]+:[^/:]+$/;

/**
 * Extracts the VID from a canonical URL path such as
 * `/nde/fulldisplay/{docid}/{vid}` or `/nde/fulldisplay/{docid}/{vid}/{lang}`.
 * Returns an empty string when no VID-shaped segment is present.
 */
function extractVidFromPath(pathname: string, prefix: string): string {
    let path = pathname;
    if (prefix && path.indexOf(prefix) === 0) {
        path = path.substring(prefix.length);
    }

    const segments = path.split('/').filter(Boolean);
    for (const segment of segments) {
        // decode in case the colon is URL-encoded (e.g. 32RUG_INST%3A32RUG_INST)
        let decoded = segment;
        try {
            decoded = decodeURIComponent(segment);
        } catch {
            // keep raw segment if it is not valid percent-encoding
        }
        if (VID_SEGMENT_REGEX.test(decoded)) {
            return decoded;
        }
    }
    return '';
}

/**
 * Initializes {@link window.__BOOTSTRAP_CFG__} using asynchronous `fetch` calls.
 *
 * This replaces the previous synchronous `XMLHttpRequest` startup logic, which triggered the
 * browser warning: "Synchronous XMLHttpRequest on the main thread is deprecated ...".
 * `main.ts` awaits this before bootstrapping Angular, so the config is fully populated before
 * any consumer (e.g. AssetPathService) reads it.
 */
export async function initBootstrapConfig(): Promise<BootstrapConfig> {
    if (window.__BOOTSTRAP_CFG__) {
        // already initialized (defensive)
        return window.__BOOTSTRAP_CFG__;
    }

    const params = new URLSearchParams(location.search);

    // simple env check: prod paths include "/nde/", dev proxy doesn't
    const isNdePath = location.pathname.indexOf('/nde/') !== -1;
    const prefix = isNdePath ? '/nde' : ''; // prod -> "/nde", dev -> ""

    // VID can be provided either as a query param (?vid=INST:VIEW) or as a path
    // segment in canonical URLs (e.g. /nde/fulldisplay/{docid}/{vid}[/{lang}]).
    let vid = params.get('vid') || '';
    let vidSource: 'query' | 'path' = 'query';
    if (!vid) {
        vid = extractVidFromPath(location.pathname, prefix);
        if (vid) {
            vidSource = 'path';
        }
    }

    const dashedVid = vid ? vid.replace(':', '-') : '';

    // The NZ code must be resolved first because it determines the Central manifest path.
    const nzVid = dashedVid ? await fetchNzVid(prefix, dashedVid) : '';

    // --- load customization asset manifests (URM-327431) ---
    // The manifest lets us resolve custom assets without probing the server (HEAD/GET) per
    // asset. `null` means no manifest is available (older packages), in which case we keep the
    // legacy probe behavior. Both loads run in parallel now that nzVid is known.
    const [izAssets, centralAssets] = await Promise.all([
        customizationEnabled && dashedVid ? loadAssetManifest(prefix, `custom/${dashedVid}`) : Promise.resolve(null),
        customizationEnabled && nzVid ? loadAssetManifest(prefix, `custom/${nzVid}-CENTRAL_PACKAGE`) : Promise.resolve(null),
    ]);

    // --- evaluate loading animation path ---
    // NOTE: the loading animation is fetched ONCE at startup (not per record), so it is not part
    // of the per-record WAF concern (URM-327431).
    const animationPath = await resolveAnimationPath(prefix, dashedVid, nzVid);

    const cfg: BootstrapConfig = {
        vid,
        dashedVid,
        nzVid,
        isNdePath,
        prefix,
        animationPath,
        izAssets,
        centralAssets,
    };
    window.__BOOTSTRAP_CFG__ = cfg;

    console.log('%c[BOOTSTRAP_CFG]', 'color:#4CAF50;font-weight:bold;');
    console.log('isNdePath:', isNdePath, '→ prefix:', prefix || '(root)');
    console.log('VID:', vid, '(source:', vidSource + ')');
    console.log('Dashed VID:', dashedVid);
    console.log('NZ VID:', nzVid);
    console.log('Animation path:', animationPath);
    console.log('IZ asset manifest:', izAssets ? `${izAssets.size} entries` : '(none)');
    console.log('Central asset manifest:', centralAssets ? `${centralAssets.size} entries` : '(none)');

    return cfg;
}

/**
 * Fetches the Network "CENTRAL_CODE.txt" for the given IZ view. Returns the trimmed NZ code, or
 * an empty string when the file is missing or the request fails.
 */
async function fetchNzVid(prefix: string, dashedVid: string): Promise<string> {
    try {
        const url = `${prefix}/custom/${dashedVid}/CENTRAL_CODE.txt`;
        const res = await fetch(url, { cache: 'no-cache' });
        if (res.ok) {
            return (await res.text()).trim();
        }
    } catch {
        // ignore; nzVid stays empty
    }
    return '';
}

/**
 * Resolves the loading-animation path by probing the IZ, Central, and bundled candidates in
 * priority order (first hit wins), mirroring the previous HEAD-probe behavior.
 */
async function resolveAnimationPath(prefix: string, dashedVid: string, nzVid: string): Promise<string> {
    const baseRel = 'assets/images/loadingAnimations/LoadingAnimationJson.json';
    if (!customizationEnabled) {
        return baseRel;
    }

    const candidatePaths: string[] = [];
    if (dashedVid) {
        candidatePaths.push(`custom/${dashedVid}/${baseRel}`);
    }
    if (nzVid) {
        candidatePaths.push(`custom/${nzVid}-CENTRAL_PACKAGE/${baseRel}`);
    }
    candidatePaths.push(baseRel);

    for (const rel of candidatePaths) {
        try {
            const url = `${prefix}/${rel}`.replace(/\/{2,}/g, '/');
            const res = await fetch(url, { method: 'HEAD', cache: 'no-store' });
            if (res.ok) {
                // strip prefix for Angular asset usage
                return rel;
            }
        } catch {
            // try the next candidate
        }
    }
    return baseRel;
}

/**
 * Asynchronously fetches a package's `asset-manifest.json` and returns the union of its file and
 * directory paths as a Set. Returns `null` when the manifest is missing or unparsable so callers
 * can fall back to the legacy per-asset probe behavior (URM-327431).
 */
async function loadAssetManifest(prefix: string, packageBase: string): Promise<Set<string> | null> {
    try {
        const url = `${prefix}/${packageBase}/asset-manifest.json`.replace(/\/{2,}/g, '/');
        const res = await fetch(url, { cache: 'no-cache' });
        if (!res.ok) {
            return null;
        }
        const parsed = await res.json();
        const files: string[] = Array.isArray(parsed.files) ? parsed.files : [];
        const directories: string[] = Array.isArray(parsed.directories) ? parsed.directories : [];
        return new Set<string>([...files, ...directories]);
    } catch {
        return null;
    }
}

// handy exports for Angular code

const DEFAULT_CFG: BootstrapConfig = {
    vid: '',
    dashedVid: '',
    nzVid: '',
    isNdePath: false,
    prefix: '',
    animationPath: 'assets/images/loadingAnimations/LoadingAnimationJson.json',
    izAssets: null,
    centralAssets: null,
};

/**
 * Returns the live bootstrap config. Because {@link initBootstrapConfig} now runs asynchronously
 * (after this module is evaluated), consumers must read the config through this accessor rather
 * than a module-load-time snapshot.
 */
export function getBootstrapCfg(): BootstrapConfig {
    return window.__BOOTSTRAP_CFG__ ?? DEFAULT_CFG;
}

export function getVid(): string {
    return getBootstrapCfg().vid;
}
export function getNzVid(): string {
    return getBootstrapCfg().nzVid;
}
export function getDashedVid(): string {
    return getBootstrapCfg().dashedVid;
}
export function getAnimationPath(): string {
    return getBootstrapCfg().animationPath;
}